import { createContext, useContext, ReactNode, useState } from 'react';

import { useMutation, useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';

import operatorSession, {
    AdditionalInfo,
    Resolution,
} from '@clients/operatorSessionClient';
import { useAccount } from '@contexts/AccountProvider';
import notifyAPIError from '@utils/notifyAPIError';

type EndSession = (additionalInfo?: AdditionalInfo) => void;

export interface OperatorSessionType {
    isLoading: boolean;
    protocol: string;
    additionalInfo: AdditionalInfo;
    addNotes: (text: string) => void;
    addResolution: (resolution: string) => void;
    sessionStartTime: dayjs.Dayjs;
    error?: Error;
    endSession: EndSession;
    isSubmitting: boolean;
    submissionError?: Error;
}

const OperatorSession = createContext<OperatorSessionType>(null);

type OperatorSessionProviderProps = {
    children: ReactNode;
};

const initialAdditionalInfo = {
    text: '',
};

const { startOperatorSession, endOperatorSession } = operatorSession();

const OperatorSessionProvider = ({
    children,
}: OperatorSessionProviderProps) => {
    const { account } = useAccount();
    const navigate = useNavigate();
    const customerId = account?.customer_id;
    const accountId = account?.account_id;
    const [protocol, setProtocol] = useState('');
    const [additionalInfo, setAdditionalInfo] = useState<AdditionalInfo>(
        initialAdditionalInfo,
    );
    const [sessionStartTime, setSessionStartTime] = useState<dayjs.Dayjs>();

    // Make query to start the session
    const { error, isLoading } = useQuery(
        ['startOperatorSession', accountId, customerId],
        () =>
            startOperatorSession({
                accountId,
                customerId,
            }),
        {
            // Disable the query until the accountId and customerId exist, Then disable it if we have a protocol, so we do not refetch it
            enabled: !!accountId && !!customerId && !protocol,
            onSuccess: (data) => {
                setSessionStartTime(dayjs());
                setProtocol(data.protocol);
            },
            onError: notifyAPIError,
        },
    );

    // mutation to end session
    const {
        mutate,
        isLoading: isSubmitting,
        error: submissionError,
    } = useMutation(
        ['patchUpdateAccountStatus'],
        () =>
            endOperatorSession({
                accountId,
                customerId,
                protocol,
                additionalInfo,
            }),
        {
            onSuccess: () => {
                setSessionStartTime(null);
                setProtocol(null);
                setAdditionalInfo(initialAdditionalInfo);
                navigate('/');
            },
            onError: notifyAPIError,
        },
    );

    // End session on unmount. This is used then the user navigates away from the page
    // TODO: only works in if the navigation is in the mf auth
    // useEffect(() => {
    //     return () => {
    //         console.log('unmounting');
    //         // TODO: this gets called twice if we have called the end session from the endcall
    //         return mutate();
    //     };
    // }, [mutate]);

    const endSession: EndSession = (additionalInfo) => {
        if (additionalInfo) setAdditionalInfo(additionalInfo);
        mutate();
    };

    const addNotes = (text: string) => {
        setAdditionalInfo({
            ...additionalInfo,
            text,
        });
    };

    const addResolution = (resolution: Resolution) => {
        setAdditionalInfo({
            ...additionalInfo,
            resolution,
        });
    };

    return (
        <OperatorSession.Provider
            value={{
                isLoading,
                protocol,
                additionalInfo,
                addNotes,
                addResolution,
                sessionStartTime,
                error: error as Error,
                isSubmitting,
                submissionError: submissionError as Error,
                endSession,
            }}>
            {children}
        </OperatorSession.Provider>
    );
};

const useOperatorSession = () => useContext(OperatorSession);

export { OperatorSessionProvider, useOperatorSession };
export default OperatorSession;
